import { Component, CUSTOM_ELEMENTS_SCHEMA, inject, OnInit } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { DropdownService } from '@core/services/';
import { Dropdown } from '@core/types/';
import { settingStore } from '@core/store/index';
import { SelectModule } from 'primeng/select';
import {effect} from "@angular/core";

interface Cloud {
  id: number;
  size: number;
  top: number;
  duration: number;
  delay: number;
  direction: 'left' | 'right';
  opacity: number;
  svgPath: string;
  parallaxFactor: number;
  depth: 'near' | 'middle' | 'far';
  colorGradient: string;
  blurAmount: number;
}

interface Language {
  code: string;
  name: string;
  flag: string;
}

@Component({
  selector: 'app-types-layout',
  standalone: true,
  templateUrl: './auth-layout.component.html',
  styleUrl: './auth-layout.component.css',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [RouterOutlet,  FormsModule, CommonModule, TranslateModule, SelectModule],
})
export class AuthLayoutComponent implements OnInit {
  setting = inject(settingStore);
  dropdownLanguage: Dropdown<string>[] = [];
  currentYear = new Date().getFullYear();

  // Variables para el efecto parallax de las nubes
  mouseX: number = 0;
  mouseY: number = 0;

  // Array de nubes generadas aleatoriamente
  clouds: Cloud[] = [];

  // Language switcher properties
  isLanguageDropdownOpen: boolean = false;
  languages: Language[] = [
    { code: 'es', name: 'Español', flag: '🇩🇴' },
    { code: 'en', name: 'English', flag: '🇺🇸' }
  ];

  constructor(private dropdownService: DropdownService, private translate: TranslateService) {
    this.setLanguageMenu();
    effect(() => {
      this.setLanguageMenu();
      this.translate.use(this.setting.data().language);
    });
  }

  ngOnInit() {
    this.generateRandomClouds();
  }

  setLanguageMenu() {
    this.dropdownLanguage = this.setting.data().language === 'en'
      ? this.dropdownService.SelectLanguageEn
      : this.dropdownService.SelectLanguageEs;
  }

  onChangeLanguage(event: any) {
    this.setting.setLanguage(event.value);
  }

  /**
   * Toggle the language dropdown visibility
   */
  toggleLanguageDropdown() {
    this.isLanguageDropdownOpen = !this.isLanguageDropdownOpen;
  }

  /**
   * Select a language and close the dropdown
   */
  selectLanguage(languageCode: string) {
    this.setting.setLanguage(languageCode);
    this.isLanguageDropdownOpen = false;
  }

  /**
   * Get the current language code in uppercase
   */
  getCurrentLanguageCode(): string {
    return this.setting.data().language.toUpperCase();
  }

  /**
   * Genera una cantidad aleatoria de nubes con sistema de profundidad de 3 capas
   */
  generateRandomClouds() {
    const numberOfClouds = this.getRandomInt(15, 25); // Más nubes para mejor efecto de profundidad
    const svgPaths = this.getCloudSvgPaths();

    for (let i = 0; i < numberOfClouds; i++) {
      // Determinar profundidad de la nube (33% para cada capa)
      const depthRandom = Math.random();
      let depth: 'near' | 'middle' | 'far';
      let size: number;
      let opacity: number;
      let blurAmount: number;
      let colorGradient: string;
      let parallaxFactor: number;
      let baseDuration: number;

      if (depthRandom < 0.33) {
        // Nubes cercanas (33% de probabilidad)
        depth = 'near';
        size = this.getRandomInt(200, 350);
        opacity = this.getRandomFloat(0.15, 0.25);
        blurAmount = 0; // Sin blur para nubes cercanas
        colorGradient = 'near'; // Gradiente más definido
        parallaxFactor = this.getRandomFloat(0.15, 0.25);
        baseDuration = this.getRandomInt(40, 60); // Más rápidas
      } else if (depthRandom < 0.66) {
        // Nubes medias (33% de probabilidad)
        depth = 'middle';
        size = this.getRandomInt(300, 500);
        opacity = this.getRandomFloat(0.10, 0.18);
        blurAmount = this.getRandomFloat(0.5, 1.5); // Blur ligero
        colorGradient = 'middle'; // Gradiente medio
        parallaxFactor = this.getRandomFloat(0.08, 0.15);
        baseDuration = this.getRandomInt(60, 90); // Velocidad media
      } else {
        // Nubes lejanas (34% de probabilidad)
        depth = 'far';
        size = this.getRandomInt(400, 700);
        opacity = this.getRandomFloat(0.05, 0.12);
        blurAmount = this.getRandomFloat(1.5, 3.0); // Más blur
        colorGradient = 'far'; // Gradiente más sutil
        parallaxFactor = this.getRandomFloat(0.02, 0.08);
        baseDuration = this.getRandomInt(80, 120); // Más lentas
      }

      const cloud: Cloud = {
        id: i,
        size: size,
        top: this.getRandomInt(5, 75), // Más espacio vertical
        duration: baseDuration,
        delay: this.getRandomInt(-60, 0), // Delay negativo para distribución inicial
        direction: Math.random() > 0.5 ? 'right' : 'left',
        opacity: opacity,
        svgPath: svgPaths[this.getRandomInt(0, svgPaths.length - 1)],
        parallaxFactor: parallaxFactor,
        depth: depth,
        colorGradient: colorGradient,
        blurAmount: blurAmount
      };

      this.clouds.push(cloud);
    }
  }

  /**
   * Retorna formas orgánicas y volumétricas de nubes realistas
   * Diseño con más detalle y variaciones naturales
   */
  getCloudSvgPaths(): string[] {
    return [
      // Nube cúmulo volumétrica #1 - Forma principal con protuberancias
      'M8,35 Q3,32 6,28 Q12,25 25,27 Q45,24 70,26 Q95,23 125,27 Q150,24 170,29 Q185,27 190,31 Q195,33 185,36 Q160,38 130,35 Q95,37 60,34 Q30,36 15,35 Q8,35 Z',

      // Nube estratos ondulada #2 - Forma más ancha con ondulaciones
      'M5,32 Q2,30 4,27 Q15,24 35,26 Q60,23 90,26 Q120,23 150,27 Q175,24 190,28 Q200,26 205,30 Q207,32 195,34 Q170,35 140,32 Q105,34 70,31 Q35,33 15,32 Q5,32 Z',

      // Nube cúmulo compacta #3 - Forma más ancha y redondeada
      'M10,38 Q5,35 7,31 Q15,28 35,30 Q60,27 90,30 Q120,27 150,31 Q175,28 185,32 Q190,35 185,38 Q165,40 135,37 Q100,39 65,36 Q35,38 15,38 Q10,38 Z',

      // Nube cirros delgada #4 - Forma más ancha y extendida
      'M5,30 Q2,28 3,26 Q8,23 28,25 Q53,22 83,25 Q113,22 143,26 Q168,23 183,27 Q193,25 198,29 Q200,31 188,33 Q163,34 133,31 Q98,33 63,30 Q28,32 8,30 Q5,30 Z',

      // Nube cúmulo con protuberancias #5 - Forma irregular
      'M12,36 Q7,33 9,29 Q18,26 38,28 Q63,25 93,28 Q123,25 153,29 Q173,26 183,30 Q188,33 183,36 Q163,38 133,35 Q98,37 63,34 Q33,36 18,36 Q12,36 Z',

      // Nube estratos con ondulaciones #6 - Forma más ancha horizontal ondulada
      'M3,31 Q1,29 2,26 Q13,23 33,25 Q58,22 88,25 Q118,22 148,26 Q168,23 178,27 Q183,29 178,32 Q158,33 128,30 Q93,32 58,29 Q28,31 13,31 Q3,31 Z',

      // Nube cúmulo con detalles #7 - Forma más ancha con sub-nubes
      'M5,37 Q1,34 2,30 Q10,27 30,29 Q55,26 85,29 Q115,26 145,30 Q165,27 175,31 Q180,34 175,37 Q155,39 125,36 Q90,38 55,35 Q25,37 10,37 Q5,37 Z',

      // Nube cirros ondulada #8 - Forma más ancha con ondulaciones
      'M3,29 Q1,27 2,24 Q7,21 27,23 Q52,20 82,23 Q112,20 142,24 Q162,21 172,25 Q177,27 172,30 Q152,31 122,28 Q87,30 52,27 Q22,29 7,29 Q3,29 Z',

      // Nube cúmulo masiva #9 - Forma grande y redondeada
      'M18,40 Q12,37 14,32 Q22,29 42,31 Q67,28 97,31 Q127,28 157,32 Q177,29 187,33 Q192,36 187,40 Q167,42 137,39 Q102,41 67,38 Q37,40 22,40 Q18,40 Z',

      // Nube estratos suave #10 - Forma más ancha horizontal suave
      'M2,33 Q1,31 1,28 Q10,25 30,27 Q55,24 85,27 Q115,24 145,28 Q165,25 175,29 Q180,31 175,34 Q155,35 125,32 Q90,34 55,31 Q25,33 10,33 Q2,33 Z',

      // Nube cúmulo con irregularidades #11 - Forma más ancha orgánica
      'M9,39 Q4,36 6,32 Q14,29 34,31 Q59,28 89,31 Q119,28 149,32 Q169,29 179,33 Q184,36 179,39 Q159,41 129,38 Q94,40 59,37 Q29,39 14,39 Q9,39 Z',

      // Nube cirros extendida #12 - Forma más ancha muy alargada
      'M2,28 Q1,26 2,24 Q3,21 23,23 Q48,20 78,23 Q108,20 138,24 Q158,21 168,25 Q173,27 168,30 Q148,31 118,28 Q83,30 48,27 Q18,29 3,28 Q2,28 Z',

      // Nube cúmulo con sub-nubes #13 - Forma más ancha compleja
      'M6,38 Q1,35 3,31 Q11,28 31,30 Q56,27 86,30 Q116,27 146,31 Q166,28 176,32 Q181,35 176,38 Q156,40 126,37 Q91,39 56,36 Q26,38 11,38 Q6,38 Z',

      // Nube estratos con variaciones #14 - Forma más ancha ondulada
      'M4,34 Q1,32 2,29 Q12,26 32,28 Q57,25 87,28 Q117,25 147,29 Q167,26 177,30 Q182,32 177,35 Q157,36 127,33 Q92,35 57,32 Q27,34 12,34 Q4,34 Z',

      // Nube cúmulo compacta con detalles #15 - Forma más ancha redondeada con textura
      'M11,39 Q6,36 8,32 Q16,29 36,31 Q61,28 91,31 Q121,28 151,32 Q171,29 181,33 Q186,36 181,39 Q161,41 131,38 Q96,40 61,37 Q31,39 16,39 Q11,39 Z'
    ];
  }

  /**
   * Genera un número entero aleatorio entre min y max (inclusivo)
   */
  getRandomInt(min: number, max: number): number {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  /**
   * Genera un número decimal aleatorio entre min y max
   */
  getRandomFloat(min: number, max: number): number {
    return Math.random() * (max - min) + min;
  }

  /**
   * Maneja el movimiento del mouse para crear efecto parallax en las nubes
   * Las nubes se moverán sutilmente siguiendo el cursor
   */
  onMouseMove(event: MouseEvent) {
    // Calcular la posición relativa del mouse respecto al centro de la pantalla
    // Los valores van de -50 a 50 aproximadamente
    const centerX = window.innerWidth / 2;
    const centerY = window.innerHeight / 2;
    
    this.mouseX = (event.clientX - centerX) / 10;
    this.mouseY = (event.clientY - centerY) / 10;
  }

}
